import { isDompeurTruck, truckUnitKind } from "./dataManagerFleetUnits.js";

// Textes des fenêtres « Retirer de la flotte » et « Remettre dans la flotte », tirés de l'aperçu du serveur.
export const retirementDayLabel = (isoDate) => (isoDate
  ? new Date(`${isoDate}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long" }) : "");
const periodLabel = (period) => (period === "evening" ? "Soir" : "Jour");

function listLabel(items) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} et ${items.at(-1)}`;
}

// Partie retirée selon l'onglet : le camion dans Camions, la remorque dans Remorques ; un dompeur ou une unité seule d'un bloc.
export function retirementPart(truck, tab) {
  if (isDompeurTruck(truck)) return "ligne";
  const kind = truckUnitKind(truck);
  if (kind === "camion") return "camion";
  if (kind === "remorque") return "remorque";
  return tab === "compartments" ? "remorque" : "camion";
}

const fullDayLabel = (isoDate) => new Date(`${isoDate}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });

// Dates de retrait sous le genre : celle de la ligne pour une unité seule ou un dompeur, celle de chaque partie d'un ensemble.
export function retirementNotes(truck) {
  const tractorOn = truck?.tractor_retired_on ? String(truck.tractor_retired_on).slice(0, 10) : "";
  const trailerOn = truck?.trailer_retired_on ? String(truck.trailer_retired_on).slice(0, 10) : "";
  const kind = truckUnitKind(truck);
  if (isDompeurTruck(truck) || kind === "camion") return tractorOn ? [`Retiré le ${fullDayLabel(tractorOn)}`] : [];
  if (kind === "remorque") return trailerOn ? [`Retirée le ${fullDayLabel(trailerOn)}`] : [];
  return [tractorOn && `Camion retiré le ${fullDayLabel(tractorOn)}`, trailerOn && `Remorque retirée le ${fullDayLabel(trailerOn)}`].filter(Boolean);
}

export function retirementUnitNames(truck) {
  return { tractor: truck?.truck_name || truck?.truck_id || "", trailer: truck?.compartment_name || truck?.truck_id || "" };
}

// Nom de ce qui est retiré ou remis : le camion, la remorque, ou « camion-remorque » pour un ensemble entier.
export function retirementLabel(truck, part) {
  const { tractor, trailer } = retirementUnitNames(truck);
  if (part === "remorque") return trailer;
  return part === "ligne" && !isDompeurTruck(truck) && truckUnitKind(truck) === "ensemble" ? `${tractor}-${trailer}` : tractor;
}

// Nom que gardent les tournées d'avant la date, la Consolidation et la facturation : « camion-remorque » pour un ensemble.
export function retirementHistoryName(truck) {
  if (truck?.display_name) return truck.display_name;
  const { tractor, trailer } = retirementUnitNames(truck);
  const kind = truckUnitKind(truck);
  if (kind === "remorque") return trailer;
  return kind === "ensemble" && !isDompeurTruck(truck) ? `${tractor}-${trailer}` : tractor;
}

export function retirementBullets(truck, preview) {
  const { tractor, trailer } = retirementUnitNames(truck);
  const from = preview.effective_from_label;
  const dompeur = isDompeurTruck(truck);
  const items = [];
  if (preview.retire_tractor) {
    const drivers = [...new Set((preview.released_drivers || []).map((item) => item.driver_name).filter(Boolean))];
    items.push(`${tractor} quitte la flotte à partir du ${from} : il n'est plus dans les Dispatchs${dompeur ? "" : " ni dans « Changer de camion »"}.`
      + (drivers.length ? ` ${listLabel(drivers)} n'${drivers.length > 1 ? "ont" : "a"} plus de camion par défaut.` : ""));
  }
  if (preview.retire_trailer && !dompeur) {
    items.push(`${trailer} quitte la flotte à partir du ${from} : elle n'est plus dans les Dispatchs.`);
  }
  if (preview.retire_tractor && !preview.retire_trailer && truckUnitKind(truck) === "ensemble" && !dompeur) {
    const waiting = (preview.waiting_dates || preview.dates || []).map(retirementDayLabel);
    items.push(`${trailer} reste dans la flotte. Sans camion, elle passe à « Camion à choisir »`
      + (waiting.length ? ` ${waiting.length > 1 ? "les" : "le"} ${listLabel(waiting)}.` : " à partir de cette date."));
  }
  const detached = preview.detached || [];
  if (detached.length) {
    items.push(`${detached.length > 1 ? "Attelages détachés" : "Attelage détaché"} : ${listLabel(detached.map((item) =>
      `${item.trailer_label} le ${retirementDayLabel(item.dispatch_date)} (${periodLabel(item.dispatch_period)})`))}. `
      + (detached.length > 1 ? "Ces remorques passent à « Camion à choisir »." : "Cette remorque passe à « Camion à choisir »."));
  }
  const freed = preview.freed_tractors || [];
  if (freed.length) {
    items.push(`${freed.length > 1 ? "Camions libérés" : "Camion libéré"} : ${listLabel(freed.map((item) =>
      `${item.tractor_name || item.tractor_truck_id} le ${retirementDayLabel(item.dispatch_date)} (${periodLabel(item.dispatch_period)})`))}.`);
  }
  const cleared = preview.cleared_drivers || [];
  if (cleared.length) {
    items.push(`${cleared.length > 1 ? "Chauffeurs retirés" : "Chauffeur retiré"} du Dispatch : ${listLabel(cleared.map((item) =>
      `${item.driver_name || "chauffeur"} (${retirementDayLabel(item.dispatch_date)}, ${periodLabel(item.dispatch_period)})`))}.`);
  }
  return items;
}
